import type { PlacedBackground } from '@/lib/backgrounds/background-placement'
import type { SpreadPageRect } from '@/lib/backgrounds/types/spread-page-rect'
import { resolveBackgroundVisual } from '@/lib/backgrounds/resolve-background-visual'
import { cn } from '@/utils'

/**
 * One placed background, tinted and clipped to its slot's bleed box (`rect`). The image is fitted
 * into `imageRect` — the same box unless that's larger (a spread background on a one-page spread;
 * see `backgroundImageRect`), in which case only the part over `rect` shows.
 */
export function BackgroundFillRect({
  rect,
  imageRect = rect,
  placed,
  isPreview = false,
}: {
  rect: SpreadPageRect
  imageRect?: SpreadPageRect
  placed: PlacedBackground
  isPreview?: boolean
}) {
  const visual = resolveBackgroundVisual(placed.background)
  const imageFillsRect =
    imageRect.x === rect.x &&
    imageRect.y === rect.y &&
    imageRect.width === rect.width &&
    imageRect.height === rect.height

  return (
    <div
      className="pointer-events-none absolute overflow-hidden"
      style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height }}
      data-background-slot-preview={isPreview || undefined}
    >
      {/* Legacy's tint is plain opacity over the paper — an `feColorMatrix` scaling only alpha by
          tint/100 (page-editor's `SvgUtil.createSvgTintFilter`, `odeditorsvg.js` ~line 16683). */}
      <div className="h-full w-full" style={{ opacity: placed.tint / 100 }}>
        {visual.kind === 'image' ? (
          // Fitted whole inside the bleed box, centered — matches legacy's editor canvas, whose
          // background `<image>` sets no `preserveAspectRatio` and so gets SVG's default
          // `xMidYMid meet` (`ODBackground.drawSvg`, `odeditorsvg.js` ~line 2514). One library
          // image serves all three book sizes, so a few-percent shape mismatch shows as thin paper
          // strips rather than a crop or a stretch. (Legacy's saved spread thumbnail stretches
          // instead — `drawImage(img, x, y, w, h)` in `odrender.js` — but that's not the editor.)
          <img
            src={visual.url}
            alt=""
            className={cn(
              'object-contain object-center',
              imageFillsRect ? 'h-full w-full' : 'absolute max-w-none'
            )}
            style={
              imageFillsRect
                ? undefined
                : {
                    left: imageRect.x - rect.x,
                    top: imageRect.y - rect.y,
                    width: imageRect.width,
                    height: imageRect.height,
                  }
            }
            decoding="async"
            draggable={false}
          />
        ) : visual.kind === 'color' ? (
          <div className="h-full w-full" style={{ backgroundColor: visual.hex }} />
        ) : null}
      </div>
    </div>
  )
}
